import React, { Component } from "react";
import PropTypes from "prop-types";

import { isDefined, noop, hexToRGBA } from "../../utils";
import { getXValue } from "../../utils/ChartDataUtil";

import { getNewXY } from "./EachTrendLine";
import HoverTextNearMouse from "../components/HoverTextNearMouse";
import ClickableCircle from "../components/ClickableCircle";
import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";

function helper(props, moreProps) {
  const { x1, y1, x2, y2 } = props;

  const {
    xScale,
    chartConfig: { yScale },
  } = moreProps;

  const [px1, px2] = [xScale(x1), xScale(x2)];
  const [py1, py2] = [yScale(y1), yScale(y2)];

  return {
    x1: px1,
    y1: py1,
    x2: px2,
    y2: py2,
  };
}

class EachCallout extends Component {
  constructor(props) {
    super(props);

    this.handleBubbleDragStart = this.handleBubbleDragStart.bind(this);
    this.handleBubbleDrag = this.handleBubbleDrag.bind(this);
    this.handleStemDrag = this.handleStemDrag.bind(this);
    this.handleDragComplete = this.handleDragComplete.bind(this);

    this.handleHover = this.handleHover.bind(this);
    this.isHover = this.isHover.bind(this);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);

    this.state = {
      hover: false,
    };
  }

  handleHover(moreProps) {
    if (this.state.hover !== moreProps.hovering) {
      this.setState({
        hover: moreProps.hovering,
      });
    }
  }

  handleBubbleDragStart() {
    const { x1, y1, x2, y2 } = this.props;
    this.dragStart = { x1, y1, x2, y2 };
  }

  handleBubbleDrag(moreProps) {
    const { index, onDragBubble } = this.props;
    const { x1, y1, x2, y2 } = this.dragStart;

    const {
      xScale,
      chartConfig: { yScale },
      xAccessor,
      fullData,
      startPos,
      mouseXY,
    } = moreProps;

    const px2 = xScale(x2);
    const py2 = yScale(y2);

    const dx = startPos[0] - mouseXY[0];
    const dy = startPos[1] - mouseXY[1];

    // Move only the bubble (x2, y2), keep bar anchor (x1, y1) fixed
    const newX2 = getXValue(xScale, xAccessor, [px2 - dx, py2 - dy], fullData);
    const newY2 = yScale.invert(py2 - dy);

    onDragBubble(index, {
      x1: x1, // Keep bar anchor fixed
      y1: y1,
      x2: newX2,
      y2: newY2,
    });
  }

  handleStemDrag(moreProps) {
    const { index, onDragStem } = this.props;

    // Move only the bar anchor (x1, y1)
    const [newX1, newY1] = getNewXY(moreProps);

    onDragStem(index, [newX1, newY1]);
  }

  handleDragComplete(moreProps) {
    const { onDragComplete } = this.props;
    onDragComplete(moreProps);
  }

  isHover(moreProps) {
    const { x1, y1, x2, y2 } = helper(this.props, moreProps);
    const { text, appearance } = this.props;
    const { mouseXY } = moreProps;
    const [mx, my] = mouseXY;

    // Check bubble area
    const fontSize = appearance?.font?.fontSize || 12;
    const textWidth = text.length * fontSize * 0.6;
    const padding = 8;
    const bubbleWidth = textWidth + padding * 2;
    const bubbleHeight = fontSize + padding * 2;

    const hoveringBubble =
      mx >= x2 - 5 &&
      mx <= x2 + bubbleWidth + 5 &&
      my >= y2 - bubbleHeight - 5 &&
      my <= y2 + 5;

    // Check if hovering near bar anchor or stem line
    const distToAnchor = Math.sqrt(Math.pow(mx - x1, 2) + Math.pow(my - y1, 2));
    const hoveringAnchor = distToAnchor < 15;

    // Check if hovering near stem line (using distance to line formula)
    const dx = x2 + bubbleWidth / 2 - x1;
    const dy = y2 - y1;
    const lineLength = Math.sqrt(dx * dx + dy * dy);

    if (lineLength > 0) {
      const distToLine = Math.abs(dy * (mx - x1) - dx * (my - y1)) / lineLength;
      const hoveringStem =
        distToLine < 5 &&
        mx >= Math.min(x1, x2) - 5 &&
        mx <= Math.max(x1, x2 + bubbleWidth) + 5 &&
        my >= Math.min(y1, y2 - bubbleHeight) - 5 &&
        my <= Math.max(y1, y2) + 5;

      return hoveringBubble || hoveringAnchor || hoveringStem;
    }

    return hoveringBubble || hoveringAnchor;
  }

  drawOnCanvas(ctx, moreProps) {
    const { selected, text, appearance } = this.props;
    const { x1, y1, x2, y2 } = helper(this.props, moreProps);

    const { stroke, fill, font } = appearance;

    const strokeColor = stroke?.color || stroke || "#000000";
    const strokeWidth = stroke?.width || 1;
    const strokeOpacity = stroke?.opacity !== undefined ? stroke.opacity : 1;

    const fillColor = fill?.color || fill || "#FFFFFF";
    const fillOpacity = fill?.opacity !== undefined ? fill.opacity : 0.9;

    const fontFamily =
      font?.fontFamily || "Helvetica Neue, Helvetica, Arial, sans-serif";
    const fontSize = font?.fontSize || 12;
    const fontColor = font?.color || "#000000";

    ctx.save();

    // Measure text to determine bubble size
    ctx.font = `${fontSize}px ${fontFamily}`;
    const textMetrics = ctx.measureText(text);
    const textWidth = textMetrics.width;
    const padding = 8;
    const bubbleWidth = textWidth + padding * 2;
    const bubbleHeight = fontSize + padding * 2;
    const cornerRadius = 5;

    // Draw stem (line from bubble to bar anchor)
    // x1,y1 = bar anchor, x2,y2 = bubble
    ctx.strokeStyle = hexToRGBA(strokeColor, strokeOpacity);
    ctx.lineWidth = strokeWidth;
    ctx.beginPath();
    ctx.moveTo(x2 + bubbleWidth / 2, y2);
    ctx.lineTo(x1, y1);
    ctx.stroke();

    // Draw bubble background (rounded rectangle) at x2, y2
    ctx.fillStyle = hexToRGBA(fillColor, fillOpacity);
    ctx.beginPath();
    ctx.moveTo(x2 + cornerRadius, y2 - bubbleHeight);
    ctx.lineTo(x2 + bubbleWidth - cornerRadius, y2 - bubbleHeight);
    ctx.quadraticCurveTo(
      x2 + bubbleWidth,
      y2 - bubbleHeight,
      x2 + bubbleWidth,
      y2 - bubbleHeight + cornerRadius,
    );
    ctx.lineTo(x2 + bubbleWidth, y2 - cornerRadius);
    ctx.quadraticCurveTo(
      x2 + bubbleWidth,
      y2,
      x2 + bubbleWidth - cornerRadius,
      y2,
    );
    ctx.lineTo(x2 + cornerRadius, y2);
    ctx.quadraticCurveTo(x2, y2, x2, y2 - cornerRadius);
    ctx.lineTo(x2, y2 - bubbleHeight + cornerRadius);
    ctx.quadraticCurveTo(
      x2,
      y2 - bubbleHeight,
      x2 + cornerRadius,
      y2 - bubbleHeight,
    );
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Draw text
    ctx.fillStyle = fontColor;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(text, x2 + padding, y2 - bubbleHeight / 2);

    ctx.restore();
  }

  renderSVG(moreProps) {
    const { text, appearance } = this.props;
    const { x1, y1, x2, y2 } = helper(this.props, moreProps);

    const { stroke, fill, font } = appearance;

    const strokeColor = stroke?.color || stroke || "#000000";
    const strokeWidth = stroke?.width || 1;
    const strokeOpacity = stroke?.opacity !== undefined ? stroke.opacity : 1;
    const strokeDasharray = stroke?.dash === "Solid" ? "none" : "5,5";

    const fillColor = fill?.color || fill || "#FFFFFF";
    const fillOpacity = fill?.opacity !== undefined ? fill.opacity : 0.9;

    const fontFamily =
      font?.fontFamily || "Helvetica Neue, Helvetica, Arial, sans-serif";
    const fontSize = font?.fontSize || 12;
    const fontColor = font?.color || "#000000";

    // Approximate text width (SVG doesn't have easy text measurement)
    const textWidth = text.length * fontSize * 0.6;
    const padding = 8;
    const bubbleWidth = textWidth + padding * 2;
    const bubbleHeight = fontSize + padding * 2;
    const cornerRadius = 5;

    // x1,y1 = bar anchor, x2,y2 = bubble position
    return (
      <g>
        {/* Stem line from bubble to bar anchor */}
        <line
          x1={x2 + bubbleWidth / 2}
          y1={y2}
          x2={x1}
          y2={y1}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />

        {/* Bubble background */}
        <rect
          x={x2}
          y={y2 - bubbleHeight}
          width={bubbleWidth}
          height={bubbleHeight}
          rx={cornerRadius}
          ry={cornerRadius}
          fill={fillColor}
          fillOpacity={fillOpacity}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={strokeDasharray}
        />

        {/* Text */}
        <text
          x={x2 + padding}
          y={y2 - bubbleHeight / 2}
          fontFamily={fontFamily}
          fontSize={fontSize}
          fill={fontColor}
          textAnchor="start"
          dominantBaseline="middle"
        >
          {text}
        </text>
      </g>
    );
  }

  render() {
    const {
      x1,
      y1,
      x2,
      y2,
      text,
      appearance,
      hoverText,
      selected,
      interactiveCursorClass,
    } = this.props;
    const { hover } = this.state;

    if (x1 === null || y1 === null || x2 === null || y2 === null) {
      return null;
    }

    const {
      enable: hoverTextEnabled,
      selectedText: hoverTextSelected,
      text: hoverTextUnselected,
      ...restHoverTextProps
    } = hoverText;

    const cursorClass =
      interactiveCursorClass || "react-stockcharts-move-cursor";

    return (
      <g>
        {/* Main callout rendering */}
        <GenericChartComponent
          isHover={this.isHover}
          onHover={this.handleHover}
          onUnHover={this.handleHover}
          onDragStart={this.handleBubbleDragStart}
          onDrag={this.handleBubbleDrag}
          onDragComplete={this.handleDragComplete}
          svgDraw={this.renderSVG}
          canvasToDraw={getMouseCanvas}
          canvasDraw={this.drawOnCanvas}
          interactiveCursorClass={cursorClass}
          selected={selected}
          enableDragOnHover={true}
          drawOn={["mousemove", "pan", "drag", "xAxisZoom"]}
        />

        {/* Bar anchor drag handle (at x1, y1) */}
        <ClickableCircle
          show={selected || hover}
          cx={x1}
          cy={y1}
          r={6}
          fill="#2196F3"
          stroke="#FFFFFF"
          strokeWidth={2}
          fillOpacity={0.8}
          strokeOpacity={1}
          interactiveCursorClass="react-stockcharts-move-cursor"
          onDrag={this.handleStemDrag}
          onDragComplete={this.handleDragComplete}
        />

        {/* Hover tooltip */}
        <HoverTextNearMouse
          show={hoverTextEnabled && hover && !selected}
          {...restHoverTextProps}
          text={hoverTextUnselected}
        />
      </g>
    );
  }
}

EachCallout.propTypes = {
  index: PropTypes.number,

  x1: PropTypes.number.isRequired,
  y1: PropTypes.number.isRequired,
  x2: PropTypes.number,
  y2: PropTypes.number,
  text: PropTypes.string.isRequired,

  appearance: PropTypes.object.isRequired,
  hoverText: PropTypes.object.isRequired,

  selected: PropTypes.bool,
  hovering: PropTypes.bool,
  interactiveCursorClass: PropTypes.string,

  onDragBubble: PropTypes.func.isRequired,
  onDragStem: PropTypes.func.isRequired,
  onDragComplete: PropTypes.func.isRequired,
  onTextChange: PropTypes.func,
};

EachCallout.defaultProps = {
  onDragBubble: noop,
  onDragStem: noop,
  onDragComplete: noop,
  onTextChange: noop,

  selected: false,
  hovering: false,
  text: "Texto",
  interactiveCursorClass: "",

  hoverText: {
    enable: false,
  },
};

export default EachCallout;
